import React, { useId, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, PlusCircle } from 'react-feather';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Button, IconButton, InputBase, MenuItem, Tooltip, Typography } from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';
import type { WidgetInstance } from '@trinity/chat';
import type {
  BlockConfig,
  RowHeights,
  SectionConfig,
  WidgetConfig,
  WidgetSpecificConfig,
} from '@trinity/chat/types/dashboard-config';
import { isLibraryWidgetType, widgetRefOfWidget } from '@trinity/chat/utils/library-widgets';
import {
  useDashboardBuilder,
  useDashboardBuilderEditing,
  useWidgetRegistry,
} from '@trinity/chat-components/containers/Dashboard/context';
import { useLibraryWidget } from '../../../../contexts/widget-library/Context.js';
import { AddItem } from '../../../Widget.Creator/Slot.AddItem.js';
import { CELL_GAP, ROW_HEIGHT, SLOT_PADDING } from '../dashboardLayoutUtils';
import { InlineTextEditor, INLINE_TEXT_CONFIG, isInlineText, textDataSource } from '../InlineTextEditor';
import { useBuilderPalette } from '../PaletteContext';
import { RowHeightHandle, type ApplyHeight } from '../RowHeightHandle';
import { ThemedMenu } from '../shared-styled';
import { WidgetPalette } from '../WidgetPalette';
import { WidgetRenderer } from '../WidgetRenderer';
import {
  acceptDrag,
  canDropIn,
  parseCellDrag,
  startCellDrag,
  useCellAddMode,
  useCellDrag,
  type CellContainer,
} from './cellDrag';
import { useCellOps } from './useCellOps';

// The page builder's one widget layout. A cell holds widgets (and, in a block, sections) in rows and
// side-by-side slots. Rows are as tall as their content unless dragged to a height. Widgets drag by
// their header to any slot of any cell, widen by dragging their right edge, and set their row's
// height by dragging their bottom edge. Used for blocks and sections on the standard canvas and for
// the patient journey's stage × dimension cells.

type WidgetEntry = { kind: 'widget'; row: number; slot: number; span: number; rowSpan: number; widget: WidgetConfig };
type SectionEntry = { kind: 'section'; row: number; slot: number; span: number; rowSpan: 1; section: SectionConfig };
export type CellEntry = WidgetEntry | SectionEntry;

const byPosition = (a: CellEntry, b: CellEntry) => a.row - b.row || a.slot - b.slot;

/** A block's own items as cell entries, in reading order. */
export function blockEntries(block: BlockConfig): CellEntry[] {
  return block.items
    .map((item): CellEntry =>
      item.type === 'widget'
        ? { kind: 'widget', row: item.row ?? 1, slot: item.slot, span: item.span, rowSpan: item.rowSpan ?? 1, widget: item.widget }
        : { kind: 'section', row: item.row ?? 1, slot: item.slot, span: item.span, rowSpan: 1, section: item.section },
    )
    .sort(byPosition);
}

/** A section's widgets as cell entries, in reading order. */
export function sectionEntries(section: SectionConfig): CellEntry[] {
  return section.widgets
    .map((w): CellEntry => ({ kind: 'widget', row: w.row ?? 1, slot: w.slot, span: w.span, rowSpan: w.rowSpan ?? 1, widget: w.widget }))
    .sort(byPosition);
}

/** Smallest row in a fixed-row (not fit-to-content) cell: one legacy row unit. */
export const FIXED_ROW_MIN_HEIGHT = ROW_HEIGHT - 2 * SLOT_PADDING;

/**
 * Where a cell's entries sit and where they don't. Stored rows can have gaps after moves, so rows
 * show packed (`displayRow`); `gaps` are the free slots in them and `firstFree` is where a new widget goes.
 */
function cellLayout(entries: CellEntry[], cols: number) {
  const rows = new Set<number>();
  const taken = new Set<string>();
  for (const e of entries) {
    for (let r = e.row; r < e.row + e.rowSpan; r++) {
      rows.add(r);
      for (let s = e.slot; s < e.slot + e.span; s++) taken.add(`${r}:${s}`);
    }
  }
  const storedRows = [...rows].sort((a, b) => a - b);
  const displayRow = new Map(storedRows.map((r, i) => [r, i + 1]));
  const lastRow = storedRows[storedRows.length - 1] ?? 0;
  const gaps = storedRows.flatMap((row) =>
    Array.from({ length: cols }, (_, k) => k + 1)
      .filter((slot) => !taken.has(`${row}:${slot}`))
      .map((slot) => ({ row, slot })),
  );
  return { storedRows, displayRow, gaps, firstFree: gaps[0] ?? { row: lastRow + 1, slot: 1 }, lastRow };
}

type PageCellProps = {
  container: CellContainer;
  entries: CellEntry[];
  /** Side-by-side slots. */
  cols: number;
  editing: boolean;
  /** Rows dragged to a height, by stored row number. */
  rowHeights?: RowHeights;
  /** Floor for every row in px (fixed-row cells); unset rows are as tall as their content. */
  rowMin?: number;
  /** Slots size to their widest widget instead of splitting the width equally. */
  autoWidth?: boolean;
  /** Bottom-edge drag sets a row's height. Off where the host sizes rows itself (the PJ grid). */
  rowResize?: boolean;
  /** False for a cell that shows but holds no content. */
  canAdd?: boolean;
  /** The cell fills its container and shrinks with it (patient journey cells, whose row height is set by the grid). */
  fill?: boolean;
  /** A block cell offers Section in its palette. */
  allowSections?: boolean;
  /** Called after Add widget places a widget (e.g. to widen it). */
  onWidgetAdded?: (widgetId: string, row: number, slot: number) => void;
  /** Extra controls on a widget's header (edit mode). */
  widgetActions?: (widget: WidgetConfig) => React.ReactNode;
  /** An empty cell in view mode. */
  emptyView?: React.ReactNode;
  /** Height of the Add Widget slot in an empty cell. */
  emptyMinHeight?: number;
  /**
   * Widgets keep a bordered, padded card in view mode too (default). Off where the cell itself is the
   * card (the patient journey grid).
   */
  framed?: boolean;
};

export function PageCell(props: PageCellProps): React.ReactElement | null {
  const { container, entries, cols, editing, canAdd = true, rowResize = true, framed = true } = props;
  const ops = useCellOps(container);
  const { dragging, setDragging } = useCellDrag();
  const gridRef = useRef<HTMLDivElement>(null);
  const [spanPreview, setSpanPreview] = useState<{ key: string; span: number } | null>(null);
  const adding = editing && canAdd;
  const addMode = useCellAddMode();
  const dropping = adding && dragging !== null && canDropIn(dragging, container);

  const drop = (e: React.DragEvent, row: number, slot: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(null);
    const drag = parseCellDrag(e.dataTransfer.getData('text/plain'));
    if (drag && canDropIn(drag, container)) ops.drop(drag, row, slot);
  };

  if (entries.length === 0) {
    if (!adding) return editing ? null : <>{props.emptyView ?? null}</>;
    return (
      <AddWidgetButton
        container={container}
        row={1}
        slot={1}
        allowSections={props.allowSections}
        onWidgetAdded={props.onWidgetAdded}
        minHeight={props.emptyMinHeight}
        onDrop={dropping ? (e) => drop(e, 1, 1) : undefined}
      />
    );
  }

  const { storedRows, displayRow, gaps, firstFree, lastRow } = cellLayout(entries, cols);

  // The rendered row heights (top to bottom) and the gap between rows, read when a height drag starts.
  const measureRows = () => {
    const style = gridRef.current ? getComputedStyle(gridRef.current) : null;
    const heights = style ? style.gridTemplateRows.split(' ').map(parseFloat).filter(Number.isFinite) : [];
    return { tracks: heights.slice(0, storedRows.length), gap: (style && parseFloat(style.rowGap)) || CELL_GAP };
  };
  const min = props.rowMin ?? 0;
  const tracks = storedRows
    .map((r) => {
      const px = props.rowHeights?.[String(r)];
      return px !== undefined ? `${px}px` : `minmax(${min}px, auto)`;
    })
    .join(' ');

  return (
    <>
      <Grid
        ref={gridRef}
        editing={editing}
        fill={props.fill ?? false}
        style={{
          gridTemplateColumns: props.autoWidth ? `repeat(${cols}, auto)` : `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: tracks,
        }}
      >
        {entries.map((entry) => {
          const key = entry.kind === 'widget' ? entry.widget.id : entry.section.id;
          const span = spanPreview?.key === key ? spanPreview.span : entry.span;
          const start = displayRow.get(entry.row) ?? 1;
          const style: React.CSSProperties = {
            gridRow: `${start} / span ${entry.rowSpan}`,
            gridColumn: `${entry.slot} / span ${span}`,
          };
          const resize: SpanResize | undefined =
            editing && cols > 1
              ? {
                  maxSpan: cols - entry.slot + 1,
                  slotWidth: () => (gridRef.current?.getBoundingClientRect().width ?? 0) / cols,
                  onPreview: (next) => setSpanPreview({ key, span: next }),
                  onDone: (next) => {
                    setSpanPreview(null);
                    if (next === entry.span) return;
                    if (entry.kind === 'widget') ops.setWidgetSpan(entry.row, entry.slot, next);
                    else ops.setSectionSpan(entry.section.id, next);
                  },
                }
              : undefined;
          const rowHeight: RowHeightDrag | undefined =
            editing && rowResize
              ? {
                  begin: () => {
                    if (entry.kind === 'section') return (px) => ops.setRowHeight(entry.row, px);
                    const { tracks, gap } = measureRows();
                    return ops.beginHeight(entry.row, entry.slot, tracks, gap);
                  },
                }
              : undefined;
          const onDropHere = dropping ? (e: React.DragEvent) => drop(e, entry.row, entry.slot) : undefined;

          return entry.kind === 'widget' ? (
            <CellWidgetCard
              key={key}
              container={container}
              entry={entry}
              editing={editing}
              style={style}
              onDropHere={onDropHere}
              resize={resize}
              rowHeight={rowHeight}
              actions={props.widgetActions}
              framed={framed}
            />
          ) : (
            <CellSectionCard
              key={key}
              blockId={container.blockId}
              entry={entry}
              editing={editing}
              style={style}
              onDropHere={onDropHere}
              resize={resize}
              rowHeight={rowHeight}
            />
          );
        })}
        {dropping &&
          gaps.map(({ row, slot }) => (
            <DropSlot
              key={`gap-${row}-${slot}`}
              style={{ gridRow: displayRow.get(row), gridColumn: slot }}
              onDrop={(e) => drop(e, row, slot)}
            />
          ))}
        {dropping &&
          Array.from({ length: cols }, (_, k) => (
            <DropSlot
              key={`new-${k}`}
              style={{ gridRow: storedRows.length + 1, gridColumn: k + 1 }}
              onDrop={(e) => drop(e, lastRow + 1, k + 1)}
            />
          ))}
        {/* Slots mode: an Add Widget tile in every free slot of the rows; once the rows are full, one slim strip adds a new row. */}
        {adding && !dropping && addMode === 'slots' &&
          (gaps.length > 0 ? (
            gaps.map(({ row, slot }) => (
              <AddWidgetButton
                key={`add--`}
                container={container}
                row={row}
                slot={slot}
                variant="slot"
                style={{ gridRow: displayRow.get(row), gridColumn: slot }}
                allowSections={props.allowSections}
                onWidgetAdded={props.onWidgetAdded}
              />
            ))
          ) : (
            <AddWidgetButton
              key="add-row"
              container={container}
              row={lastRow + 1}
              slot={1}
              variant="strip"
              style={{ gridRow: storedRows.length + 1, gridColumn: '1 / -1' }}
              allowSections={props.allowSections}
              onWidgetAdded={props.onWidgetAdded}
            />
          ))}
      </Grid>
      {adding && !dropping && addMode === 'button' && (
        <AddWidgetButton
          container={container}
          row={firstFree.row}
          slot={firstFree.slot}
          variant="button"
          allowSections={props.allowSections}
          onWidgetAdded={props.onWidgetAdded}
        />
      )}
    </>
  );
}

// ─── Widget card ─────────────────────────────────────────────────────────────

type SpanResize = {
  maxSpan: number;
  slotWidth: () => number;
  onPreview: (span: number) => void;
  onDone: (span: number) => void;
};

/** Starts a height drag on an entry: measured when the pointer goes down, applied on every move. */
type RowHeightDrag = { begin: () => ApplyHeight };

/** Rich text is edited in place where the builder does inline editing (otherwise in the sidebar). */
function useEditsInPlace(widget: WidgetConfig, editing: boolean): boolean {
  const inline = useBuilderPalette()?.textEditing === 'inline';
  return editing && inline && isInlineText(widget);
}

function CellWidgetCard({
  container,
  entry,
  editing,
  style,
  onDropHere,
  resize,
  rowHeight,
  actions,
  framed,
}: {
  container: CellContainer;
  entry: WidgetEntry;
  editing: boolean;
  /** Bordered, padded card in view mode too (see PageCell `framed`). */
  framed: boolean;
  style: React.CSSProperties;
  onDropHere?: (e: React.DragEvent) => void;
  resize?: SpanResize;
  rowHeight?: RowHeightDrag;
  actions?: (widget: WidgetConfig) => React.ReactNode;
}): React.ReactElement {
  const { setSelectedBlockId, setSelectedWidgetId, setSidebarPanel } = useDashboardBuilder();
  const { setDragging } = useCellDrag();
  const ops = useCellOps(container);
  const sidebarEditing = useBuilderPalette()?.textEditing !== 'inline';
  const rootRef = useRef<HTMLDivElement>(null);
  const [over, setOver] = useState(false);
  const { widget } = entry;
  const editsInPlace = useEditsInPlace(widget, editing);
  // Saved widgets are replaced (remove, then add), not configured; rich text edits in place.
  const configurable =
    !isLibraryWidgetType(widget.type) && !editsInPlace && (sidebarEditing || widget.type === 'text');

  return (
    <CardRoot
      ref={rootRef}
      editing={editing}
      framed={framed}
      over={over}
      style={style}
      {...(onDropHere && {
        onDragOver: (e: React.DragEvent) => {
          acceptDrag(e);
          setOver(true);
        },
        onDragLeave: () => setOver(false),
        onDrop: (e: React.DragEvent) => {
          setOver(false);
          onDropHere(e);
        },
      })}
    >
      {editing && (
        <CardToolbar
          draggable
          onDragStart={(e) =>
            startCellDrag(
              e,
              { kind: 'cell-widget', blockId: container.blockId, sectionId: container.sectionId ?? null, row: entry.row, slot: entry.slot },
              rootRef.current,
              setDragging,
            )
          }
          onDragEnd={() => setDragging(null)}
        >
          <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
          <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
            <WidgetLabel widget={widget} />
          </Typography>
          {configurable && (
            <Tooltip title="Configure widget">
              <IconButton
                size="small"
                aria-label="Configure widget"
                onClick={() => {
                  setSelectedBlockId(container.blockId);
                  setSelectedWidgetId(widget.id);
                  setSidebarPanel('chart-config');
                }}
              >
                <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {actions?.(widget)}
          <Tooltip title="Remove widget">
            <IconButton size="small" aria-label="Remove widget" onClick={() => ops.removeWidget(entry.row, entry.slot)}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </CardToolbar>
      )}
      <CardBody editing={editing} framed={framed}>
        {editsInPlace ? (
          <InlineTextEditor widget={widget} />
        ) : (
          <WidgetRenderer
            widgetId={widget.id}
            config={widget}
            width={entry.span}
            height={entry.rowSpan}
            blockId={container.blockId}
            row={entry.row}
            slot={entry.slot}
          />
        )}
      </CardBody>
      {resize && <SpanHandle span={entry.span} rootRef={rootRef} resize={resize} />}
      {rowHeight && <RowHeightHandle begin={rowHeight.begin} />}
    </CardRoot>
  );
}

/** A widget's header label: its title, else a saved widget's own title, else its type's name. */
function WidgetLabel({ widget }: { widget: WidgetConfig }): React.ReactElement {
  const ref = widgetRefOfWidget(widget);
  if (widget.config.title || !ref) return <TypeLabel widget={widget} />;
  return <SavedWidgetLabel widgetRef={ref} />;
}

function TypeLabel({ widget }: { widget: WidgetConfig }): React.ReactElement {
  const { registry } = useWidgetRegistry();
  return <>{widget.config.title || registry.get(widget.type)?.label || 'Widget'}</>;
}

function SavedWidgetLabel({ widgetRef }: { widgetRef: { source: 'project_library' | 'library'; widgetId: number } }): React.ReactElement {
  const state = useLibraryWidget(widgetRef);
  return <>{(state.status === 'ready' && state.widget.title) || 'Saved widget'}</>;
}

// ─── Section card ────────────────────────────────────────────────────────────

/** A section in a block's cell: its header (title, menu, drag handle) and its own cell of widgets. */
function CellSectionCard({
  blockId,
  entry,
  editing,
  style,
  onDropHere,
  resize,
  rowHeight,
}: {
  blockId: string;
  entry: SectionEntry;
  editing: boolean;
  style: React.CSSProperties;
  onDropHere?: (e: React.DragEvent) => void;
  resize?: SpanResize;
  rowHeight?: RowHeightDrag;
}): React.ReactElement {
  const { section } = entry;
  const { updateSectionTitle, toggleSectionHideTitle, toggleSectionCollapsible, updateSectionSizing } =
    useDashboardBuilderEditing();
  const ops = useCellOps({ blockId });
  const { setDragging } = useCellDrag();
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [collapsed, setCollapsed] = useState(!editing && section.collapsible === true && section.defaultCollapsed === true);
  const [over, setOver] = useState(false);
  // Fit-to-content is the default; only an explicit `autoHeight: false` keeps fixed rows.
  const autoHeight = section.autoHeight !== false;
  const autoWidth = section.autoWidth ?? false;
  const showTitle = editing || (!section.hideTitle && section.title.trim() !== '');
  const close = () => setMenuAnchor(null);

  return (
    <SectionRoot
      ref={rootRef}
      editing={editing}
      over={over}
      style={{
        ...style,
        ...(section.minHeight !== undefined && { minHeight: section.minHeight }),
        ...(section.maxHeight !== undefined && { maxHeight: section.maxHeight, overflowY: 'auto' }),
      }}
      {...(onDropHere && {
        onDragOver: (e: React.DragEvent) => {
          acceptDrag(e);
          setOver(true);
        },
        onDragLeave: () => setOver(false),
        onDrop: (e: React.DragEvent) => {
          setOver(false);
          onDropHere(e);
        },
      })}
    >
      {showTitle && (
        <SectionHeader
          editing={editing}
          draggable={editing}
          onDragStart={(e) =>
            startCellDrag(e, { kind: 'cell-section', blockId, row: entry.row, slot: entry.slot }, rootRef.current, setDragging)
          }
          onDragEnd={() => setDragging(null)}
        >
          {editing && <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />}
          {editing ? (
            <SectionTitleInput
              value={section.title}
              placeholder="Section name..."
              inputProps={{ 'aria-label': 'Section title' }}
              onChange={(e) => updateSectionTitle(blockId, section.id, e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
            />
          ) : (
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
              {section.title}
            </Typography>
          )}
          {!editing && section.collapsible && (
            <IconButton size="small" aria-label={collapsed ? 'Expand section' : 'Collapse section'} onClick={() => setCollapsed((c) => !c)}>
              {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </IconButton>
          )}
          {editing && (
            <>
              <IconButton
                size="small"
                aria-label="Section actions"
                aria-controls={menuAnchor ? menuId : undefined}
                aria-haspopup="true"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                <MoreHorizIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <ThemedMenu id={menuId} anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={close} MenuListProps={{ dense: true }}>
                <MenuItem onClick={() => { close(); toggleSectionHideTitle(blockId, section.id); }}>
                  {section.hideTitle ? 'Show title' : 'Hide title'}
                </MenuItem>
                <MenuItem onClick={() => { close(); toggleSectionCollapsible(blockId, section.id); }}>
                  {section.collapsible ? 'Remove collapsible' : 'Make collapsible'}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    close();
                    updateSectionSizing(blockId, section.id, {
                      autoHeight: autoHeight ? false : undefined,
                      minHeight: undefined,
                      maxHeight: undefined,
                      rowHeights: undefined,
                    });
                  }}
                >
                  {autoHeight ? 'Use fixed row height' : 'Fit height to content'}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    close();
                    updateSectionSizing(blockId, section.id, { autoWidth: autoWidth ? undefined : true });
                  }}
                >
                  {autoWidth ? 'Use equal column widths' : 'Size columns to content'}
                </MenuItem>
                <MenuItem onClick={() => { close(); ops.removeSection(section.id); }} sx={{ color: 'error.main' }}>
                  Delete section
                </MenuItem>
              </ThemedMenu>
            </>
          )}
        </SectionHeader>
      )}
      {!collapsed && (
        // Scrolls if the section is given less height than its widgets need (a pinned row, a max
        // height), instead of drawing over whatever sits below it.
        <SectionBody>
          <PageCell
            container={{ blockId, sectionId: section.id }}
            entries={sectionEntries(section)}
            cols={Math.max(1, section.columns ?? entry.span)}
            editing={editing}
            rowHeights={autoHeight ? section.rowHeights : undefined}
            rowMin={autoHeight ? undefined : FIXED_ROW_MIN_HEIGHT}
            rowResize={autoHeight}
            autoWidth={autoWidth}
            emptyMinHeight={120}
          />
        </SectionBody>
      )}
      {resize && <SpanHandle span={entry.span} rootRef={rootRef} resize={resize} />}
      {rowHeight && <RowHeightHandle begin={rowHeight.begin} />}
    </SectionRoot>
  );
}

// ─── Handles, add, drop ──────────────────────────────────────────────────────

/** Right-edge handle: drag across the cell's slots to set how many this entry spans. */
function SpanHandle({
  span,
  rootRef,
  resize,
}: {
  span: number;
  rootRef: React.RefObject<HTMLDivElement>;
  resize: SpanResize;
}): React.ReactElement {
  const drag = useRef<{ left: number; slotWidth: number; span: number } | null>(null);
  return (
    <SpanEdge
      role="separator"
      aria-orientation="vertical"
      aria-label="Drag to span more or fewer slots"
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const slotWidth = resize.slotWidth();
        const left = rootRef.current?.getBoundingClientRect().left;
        if (!slotWidth || left === undefined) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { left, slotWidth, span };
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        const next = Math.max(1, Math.min(resize.maxSpan, Math.round((e.clientX - d.left) / d.slotWidth)));
        if (next !== d.span) {
          d.span = next;
          resize.onPreview(next);
        }
      }}
      onPointerUp={(e) => {
        const d = drag.current;
        drag.current = null;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
        // A widget it now overlaps in that row moves down a row.
        resize.onDone(d?.span ?? span);
      }}
    />
  );
}

/**
 * Adds a widget at (row, slot) from the builder's palette. Rich text goes in inline where the builder
 * edits inline; other built-in types open the sidebar.
 * - `empty`: the whole of an empty cell; also a drop target while something is dragged.
 * - `slot`: a tile in one free slot of the grid (`style` places it).
 * - `button`: the small "+ Add widget" under the widgets.
 */
function AddWidgetButton({
  container,
  row,
  slot,
  variant = 'empty',
  style,
  allowSections = false,
  onWidgetAdded,
  minHeight = 160,
  onDrop,
}: {
  container: CellContainer;
  row: number;
  slot: number;
  variant?: 'empty' | 'slot' | 'strip' | 'button';
  style?: React.CSSProperties;
  allowSections?: boolean;
  onWidgetAdded?: (widgetId: string, row: number, slot: number) => void;
  minHeight?: number;
  onDrop?: (e: React.DragEvent) => void;
}): React.ReactElement {
  const {
    setSelectedBlockId,
    setSelectedWidgetId,
    setSidebarPanel,
    setPendingSlot,
    setPendingWidgetType,
    setPendingWidgetConfig,
  } = useDashboardBuilder();
  const { addSection } = useDashboardBuilderEditing();
  const ops = useCellOps(container);
  const palette = useBuilderPalette();
  const inline = palette?.textEditing === 'inline';
  const [open, setOpen] = useState(false);
  const [over, setOver] = useState(false);
  const inBlock = container.sectionId === undefined;

  const added = (widgetId: string) => onWidgetAdded?.(widgetId, row, slot);

  const select = (type: string, config?: Partial<WidgetSpecificConfig>) => {
    setOpen(false);
    if (type === 'section' && inBlock) {
      addSection(container.blockId, row, slot, 1);
      return;
    }
    if (type === 'text' && inline) {
      added(ops.addWidget(row, slot, 'text', INLINE_TEXT_CONFIG, textDataSource('')));
      return;
    }
    if (inBlock && !inline) {
      // Sidebar builders configure a new widget before it's placed.
      setPendingSlot({ blockId: container.blockId, row, slot });
      setPendingWidgetType(type);
      setPendingWidgetConfig(config ?? null);
      setSidebarPanel('create-widget');
      return;
    }
    const widgetId = ops.addWidget(row, slot, type, config ?? {});
    added(widgetId);
    setSelectedBlockId(container.blockId);
    setSelectedWidgetId(widgetId);
    setSidebarPanel('chart-config');
  };

  let control: React.ReactElement;
  if (variant === 'button') {
    control = (
      <AddMore>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Add widget
        </Button>
      </AddMore>
    );
  } else if (variant === 'slot') {
    control = (
      <SlotTile style={style}>
        <AddTile type="button" onClick={() => setOpen(true)}>
          <PlusCircle size={20} aria-hidden />
          Add Widget
        </AddTile>
      </SlotTile>
    );
  } else if (variant === 'strip') {
    control = (
      <SlotTile style={style}>
        <AddStrip type="button" onClick={() => setOpen(true)}>
          <PlusCircle size={18} aria-hidden />
          Add Widget
        </AddStrip>
      </SlotTile>
    );
  } else {
    control = (
      <EmptyBody
        over={over}
        {...(onDrop && {
          onDragOver: (e: React.DragEvent) => {
            acceptDrag(e);
            setOver(true);
          },
          onDragLeave: () => setOver(false),
          onDrop: (e: React.DragEvent) => {
            setOver(false);
            onDrop(e);
          },
        })}
      >
        <AddItem css={{ minHeight }} text="Add Widget" onClick={() => setOpen(true)} />
      </EmptyBody>
    );
  }

  return (
    <>
      {control}
      <WidgetPalette
        open={open}
        hideSections={!allowSections}
        onClose={() => setOpen(false)}
        onSelect={select}
        onSelectLibraryWidget={(widget: WidgetInstance) => {
          setOpen(false);
          if (!palette) return;
          const { type, config, dataSource } = palette.toPageWidget(widget);
          added(ops.addWidget(row, slot, type, config, dataSource));
        }}
      />
    </>
  );
}

function DropSlot({ style, onDrop }: { style: React.CSSProperties; onDrop: (e: React.DragEvent) => void }): React.ReactElement {
  const [over, setOver] = useState(false);
  return (
    <DropZone
      style={style}
      over={over}
      aria-hidden
      onDragOver={(e) => {
        acceptDrag(e);
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        onDrop(e);
      }}
    />
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Grid = styled('div', {
  shouldForwardProp: (p) => p !== 'editing' && p !== 'fill',
})<{ editing: boolean; fill: boolean }>(({ editing, fill }) => ({
  display: 'grid',
  gridAutoRows: 'auto',
  // Every entry in a row fills the row's height.
  alignItems: 'stretch',
  gap: CELL_GAP,
  padding: 10,
  minWidth: 0,
  // `fill`: take the container's height and shrink with it, so a cell given a fixed height (a patient
  // journey row dragged shorter) shares it across its rows and the widgets fit to that. Otherwise the
  // grid is as tall as its rows, and the container (a section) scrolls if it has less room.
  flex: fill ? '1 1 auto' : '0 0 auto',
  minHeight: fill ? 0 : undefined,
}));

const CardRoot = styled('div', {
  shouldForwardProp: (p) => p !== 'editing' && p !== 'over' && p !== 'framed',
})<{
  editing: boolean;
  over: boolean;
  framed: boolean;
}>(({ theme, editing, over, framed }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  minHeight: 0,
  ...((editing || framed) && {
    border: `1px solid ${over ? theme.palette.primary.main : theme.palette.gray.light2}`,
    borderRadius: editing ? 4 : 8,
    backgroundColor: theme.palette.white.main,
  }),
}));

const CardToolbar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  padding: '2px 4px 2px 6px',
  borderBottom: `1px solid ${theme.palette.gray.light2}`,
  flexShrink: 0,
  cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
}));

const CardBody = styled('div', {
  shouldForwardProp: (p) => p !== 'editing' && p !== 'framed',
})<{
  editing: boolean;
  framed: boolean;
}>(({ editing, framed }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  padding: cardPadding(editing, framed),
  // Content that can't shrink into the card (e.g. rich text in a short row) scrolls instead of being cut off.
  overflowX: 'hidden',
  overflowY: 'auto',
  // Whatever the widget renders fills the card, so resizing the widget, its row or its block resizes
  // the content too (as charts do).
  '& > *': { flex: '1 1 auto', minHeight: 0 },
}));

/** Space between a widget and its card's border: a framed card in view mode gets more room. */
function cardPadding(editing: boolean, framed: boolean): number {
  if (editing) return 8;
  return framed ? 12 : 0;
}

const SectionRoot = styled('div', { shouldForwardProp: (p) => p !== 'editing' && p !== 'over' })<{
  editing: boolean;
  over: boolean;
}>(({ theme, editing, over }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  minHeight: 0,
  borderRadius: 6,
  border: `1px ${editing ? 'dashed' : 'solid'} ${over ? theme.palette.primary.main : theme.palette.gray.light2}`,
  backgroundColor: editing ? `${theme.palette.primary.main}06` : 'transparent',
}));

const SectionBody = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  overflowX: 'hidden',
  overflowY: 'auto',
});

const SectionHeader = styled('div', { shouldForwardProp: (p) => p !== 'editing' })<{ editing: boolean }>(
  ({ theme, editing }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 8px 4px 10px',
    flexShrink: 0,
    ...(editing && {
      borderBottom: `1px solid ${theme.palette.gray.light2}`,
      cursor: 'grab',
      '&:active': { cursor: 'grabbing' },
    }),
  }),
);

const SectionTitleInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  '& input': {
    padding: 0,
    cursor: 'text',
    fontWeight: 700,
    fontSize: theme.typography.subtitle1.fontSize,
    color: theme.palette.text.primary,
  },
}));

const DropZone = styled('div', { shouldForwardProp: (p) => p !== 'over' })<{ over: boolean }>(({ theme, over }) => ({
  minHeight: 48,
  borderRadius: 4,
  border: `2px dashed ${over ? theme.palette.primary.main : theme.palette.gray.light2}`,
  backgroundColor: over ? `${theme.palette.primary.main}14` : 'transparent',
}));

const EmptyBody = styled('div', { shouldForwardProp: (p) => p !== 'over' })<{ over: boolean }>(({ theme, over }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  padding: 10,
  borderRadius: 4,
  outline: over ? `2px dashed ${theme.palette.primary.main}` : 'none',
  outlineOffset: -4,
}));

const SpanEdge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: -5,
  width: 8,
  height: '100%',
  zIndex: 3,
  cursor: 'ew-resize',
  touchAction: 'none',
  borderRadius: 4,
  '&:hover, &:active': { backgroundColor: `${theme.palette.primary.main}33` },
}));

/** Height of an Add Widget tile in a free slot. */
const SLOT_TILE_MIN_HEIGHT = 120;

const SlotTile = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  minHeight: 0,
});

const addTileStyles = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  border: `1px dashed ${theme.palette.gray.light2}`,
  borderRadius: 8,
  backgroundColor: theme.palette.gray.light2,
  color: theme.palette.gray.dark1,
  font: 'inherit',
  fontSize: 14,
  cursor: 'pointer',
  transition: 'color 150ms, background-color 150ms, border-color 150ms',
  '&:hover, &:focus-visible': {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}0F`,
  },
});

/** An Add Widget tile filling a free slot in a row. */
const AddTile = styled('button')(({ theme }) => ({
  ...addTileStyles(theme),
  flexDirection: 'column',
  gap: 4,
  flex: 1,
  minHeight: SLOT_TILE_MIN_HEIGHT,
}));

/** A slim Add Widget bar across every slot, for a new row. */
const AddStrip = styled('button')(({ theme }) => ({
  ...addTileStyles(theme),
  gap: 8,
  height: 44,
}));

const AddMore = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  padding: '0 4px 6px',
  flexShrink: 0,
});
